/**
 * ReportDemoPanel — C082 리포트 생성 데모 (queue #646 §R646-2 항목3)
 * 고정 데이터셋(사용자 목록 5행)을 EXCEL/PDF 로 export → 파일 다운로드. read-only.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReportExport } from '../../hooks/useReportDemo';
import type { ReportExportFormat } from '../../types/showcase';
import { cardStyle, sectionTitleStyle, subtitleStyle, labelStyle, inputStyle, primaryButtonStyle, DemoErrorState, DemoEmptyHint } from './demoShared';

export function ReportDemoPanel() {
  const { t } = useTranslation();
  const exportMutation = useReportExport();
  const [format, setFormat] = useState<ReportExportFormat>('EXCEL');

  function handleExport() {
    exportMutation.mutate(format, {
      onSuccess: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `showcase-report-demo.${format === 'EXCEL' ? 'xlsx' : 'pdf'}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      },
    });
  }

  return (
    <section aria-labelledby="report-panel-title">
      <h2 id="report-panel-title" style={sectionTitleStyle}>{t('showcase.demo.report.title')}</h2>
      <p style={subtitleStyle}>{t('showcase.demo.report.subtitle')}</p>

      <div style={cardStyle}>
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <label htmlFor="report-format" style={labelStyle}>{t('showcase.demo.report.formatLabel')}</label>
          <select id="report-format" value={format} onChange={(e) => setFormat(e.target.value as ReportExportFormat)} style={inputStyle}>
            <option value="EXCEL">EXCEL</option>
            <option value="PDF">PDF</option>
          </select>
        </div>

        <button
          type="button"
          onClick={handleExport}
          disabled={exportMutation.isPending}
          aria-busy={exportMutation.isPending}
          style={{ ...primaryButtonStyle, opacity: exportMutation.isPending ? 0.6 : 1 }}
        >
          {exportMutation.isPending ? t('showcase.demo.report.exporting') : t('showcase.demo.report.exportButton')}
        </button>

        <div style={{ marginTop: 'var(--spacing-4)' }}>
          {exportMutation.isError && (
            <DemoErrorState title={t('showcase.demo.common.callFailed')} error={exportMutation.error} />
          )}
          {!exportMutation.isError && !exportMutation.isSuccess && <DemoEmptyHint text={t('showcase.demo.report.empty')} />}
          {exportMutation.isSuccess && (
            <p role="status" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-status-success)' }}>
              {t('showcase.demo.report.downloaded')}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
